import { useState, useEffect, useMemo } from 'react';
import { api } from '../../services/apiService';

const isEmOperacao  = (nome) => { const l = (nome || '').toLowerCase(); return l.startsWith('em ') && !l.includes('manu'); };
const isInoperante  = (nome) => (nome || '').toLowerCase().includes('inop');

export default function useAlocacoes({ enabled = true } = {}) {
  const [trens, setTrens] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filtroLinha, setFiltroLinha] = useState(null);
  const [filtroStatus, setFiltroStatus] = useState(null);
  const [alocacoes, setAlocacoes] = useState({});
  const [alocacoesIniciais, setAlocacoesIniciais] = useState({});
  const [tentativa, setTentativa] = useState(0);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const closeSnackbar = () => setSnackbar((prev) => ({ ...prev, open: false }));

  const [linhas, setLinhas] = useState([]);
  const [statusList, setStatusList] = useState([]);

  const toggleField = (tremId, field, value) => {
    setAlocacoes((prev) => ({
      ...prev,
      [tremId]: {
        ...prev[tremId],
        [field]: prev[tremId]?.[field] === value ? null : value,
      },
    }));
  };

  const temMarcador = (val) =>
    !!(val?.linha || val?.inoperante || val?.status);

  const selectUnico = (tremId, field, value) => {
    setAlocacoes((prev) => {
      const atual = prev[tremId];
      const isDesmarcado = atual?.[field] === value;
      // Exige pelo menos um marcador por trem: não permitir desmarcar o único selecionado.
      if (isDesmarcado && temMarcador(atual)) {
        return prev;
      }
      return {
        ...prev,
        [tremId]: {
          linha: null,
          status: null,
          inoperante: null,
          [field]: value,
        },
      };
    });
  };


  useEffect(() => {
    if (!enabled) {
      setLoading(false);
      return;
    }

    const init = async () =>{
      setLoading(true);
      setError(null);
      try {
        const [resTrens, resLinhas, resStatus] = await Promise.all([
          api.trem.getAll(1,100),
          api.linhas.getAll(),
          api.status.getAll(),
        ]);

        const listaLinhas  = (resLinhas?.data || []).map((t) => ({ id: t.id, nome: t.linha }));
        const listaStatus  = (resStatus?.data || []).map((t) => ({ id: t.id, nome: t.statusTrem }));
        const itensTrens   = (resTrens?.data?.items || resTrens?.items || []);
        const listaTrens   = itensTrens.map((t) => ({ id: t.id, nome: t.nomeCodigo }));

        setLinhas(listaLinhas);
        setStatusList(listaStatus);
        setTrens(listaTrens);

        const initial = {};
        itensTrens.forEach((t) => {
          const linhaNome    = listaLinhas.find((l) => l.id === t.linhaId)?.nome ?? null;
          const linhaNomeStr = linhaNome != null ? String(linhaNome) : null;
          const statusNome   = listaStatus.find((s) => s.id === t.statusTrem)?.nome ?? null;

          if (linhaNomeStr && isEmOperacao(statusNome)) {
            initial[t.id] = { linha: linhaNomeStr, status: null, inoperante: null };
          } else if (linhaNomeStr && isInoperante(statusNome)) {
            initial[t.id] = { linha: null, status: null, inoperante: linhaNomeStr };
          } else {
            initial[t.id] = { linha: null, status: statusNome, inoperante: null };
          }
        });
        setAlocacoes(initial);
        setAlocacoesIniciais(initial);
      } catch (error) {
        setError('Erro ao carregar dados. Verifique a conexão.');
      } finally {
        setLoading(false);
      }
    }
    init();
  }, [tentativa, enabled]);

  const filteredTrens = useMemo(
    () =>
      trens.filter((t) => {
        if (filtroLinha) {
          const linhaAtual = alocacoes[t.id]?.linha ?? alocacoes[t.id]?.inoperante ?? null;

          if (filtroLinha === 'semLinha' && linhaAtual !== null) return false;
          if (filtroLinha !== 'semLinha' && String(linhaAtual) !== String(filtroLinha)) return false;
        }
        if (filtroStatus) {
          const statusAtual = alocacoes[t.id]?.status ?? null;
          if (filtroStatus === '' && statusAtual !== null) return false;
          if (filtroStatus !== '' && statusAtual !== filtroStatus) return false;
        }
        return true;
      }),
    [trens, filtroLinha, filtroStatus, alocacoes],
  );
  const handleConfirmar = async () => {
    const semMarcador = Object.entries(alocacoes).find(([, val]) => !temMarcador(val));
    if (semMarcador) {
      setSnackbar({
        open: true,
        message: 'Cada trem precisa ter exatamente uma opção marcada (linha em operação, inoperante ou outro status).',
        severity: 'warning',
      });
      return;
    }

    const promises = Object.entries(alocacoes)
      .map(([tremId, val]) => {
        let linhaId = null;
        let statusId = null;

        if (val.linha) {
          linhaId  = linhas.find((l) => l.nome == val.linha)?.id ?? null;
          statusId = statusList.find((s) => isEmOperacao(s.nome))?.id ?? null;
        } else if (val.inoperante) {
          linhaId  = linhas.find((l) => l.nome == val.inoperante)?.id ?? null;
          statusId = statusList.find((s) => isInoperante(s.nome))?.id ?? null;
        } else if (val.status) {
          statusId = statusList.find((s) => s.nome === val.status)?.id ?? null;
        }

        return api.trem.alocar(Number(tremId), {
          linhaId,
          statusTrem: statusId,
        });
      });

    try {
      await Promise.all(promises);
      setAlocacoesIniciais(
        Object.fromEntries(
          Object.entries(alocacoes).map(([tremId, val]) => [
            tremId,
            { linha: val?.linha ?? null, status: val?.status ?? null },
          ]),
        ),
      );
      setSnackbar({ open: true, message: 'Alocações salvas com sucesso!', severity: 'success' });
    } catch (err) {
      setSnackbar({ open: true, message: 'Erro ao salvar alocações. Tente novamente.', severity: 'error' });
    }
  };
  return {
    loading,
    error,
    alocacoes,
    linhas,
    statusList,
    filteredTrens,
    filtroLinha,
    filtroStatus,
    setFiltroLinha,
    setFiltroStatus,
    toggleField,
    selectUnico,
    handleConfirmar,
    snackbar,
    closeSnackbar,
    retry: () => setTentativa((n) => n + 1),
  };
}
